import * as React from 'react';
import * as styles from './TimeStreamMiniMap.scss';
import { getAssetsBoundingRect, getShowMiniMap, max, min } from '../viewstate';
import { observer } from 'mobx-react-lite';
import TimeStreamMinimapAssets from './TimeStreamMinimapAssets';
import classNames from 'classnames';
import getCanvasDocumentBounds from '../util/getCanvasDocumentBounds';

const hiddenMiniMap = classNames(styles.miniMap, styles.hiddenMiniMap);

// Use different zoom sizes so the minimap does not take too much space
// on the canvas when the canvas is too small (or browser zoom is too big).
const zoomFactors: [number, number, React.CSSProperties?][] = [
    [350, 150, { display: 'none' }],
    [450, 200, { zoom: 0.25 }],
    [650, 380, { zoom: 0.5 }],
    [Infinity, Infinity, undefined],
];

export default observer(function TimeStreamMiniMap() {
    const className = getShowMiniMap() ? styles.miniMap : hiddenMiniMap;

    const {
        canvasTop,
        canvasLeft,
        canvasBottom,
        canvasRight,
        canvasHeight,
        canvasWidth,
        canvasElementHeight,
        canvasElementWidth,
    } = getCanvasDocumentBounds();

    const assets = getAssetsBoundingRect();

    // Union of the canvas and asset bounding rectangles and some padding.
    // These are the document coordinates that the SVG view box will look at.
    const padding = 100;
    const top = Math.floor(min(canvasTop, assets.top)) - padding;
    const left = Math.floor(min(canvasLeft, assets.left)) - padding;
    const bottom = Math.ceil(max(canvasBottom, assets.bottom)) + padding;
    const right = Math.ceil(max(canvasRight, assets.right)) + padding;
    const viewBoxWidth = right - left;
    const viewBoxHeight = bottom - top;

    // One of the sides of the mini map will be fixed at 250px.
    // The other side adapts so we have the same ratio as the full rectangle.
    const fixedSide = 250;
    const isTall = viewBoxHeight > viewBoxWidth;
    const mapHeight = isTall ? fixedSide : (fixedSide * viewBoxHeight) / viewBoxWidth;
    const mapWidth = isTall ? (fixedSide * viewBoxWidth) / viewBoxHeight : fixedSide;

    const topShadeHeight = canvasTop - top;
    const leftShadeWidth = canvasLeft - left;
    const rightShadeWidth = right - canvasRight;
    const bottomShadeHeight = bottom - canvasBottom;

    const style = zoomFactors.filter(
        ([maxWidth, maxHeight]) =>
            canvasElementWidth <= maxWidth || canvasElementHeight <= maxHeight
    )[0][2];

    return (
        <svg
            className={className}
            style={style}
            width={mapWidth}
            height={mapHeight}
            viewBox={`${left} ${top} ${viewBoxWidth} ${viewBoxHeight}`}>
            <TimeStreamMinimapAssets />
            <g className={styles.shade}>
                <rect x={left} y={top} width={viewBoxWidth} height={topShadeHeight} />
                <rect x={left} y={canvasTop} width={leftShadeWidth} height={canvasHeight} />
                <rect x={canvasRight} y={canvasTop} width={rightShadeWidth} height={canvasHeight} />
                <rect x={left} y={canvasBottom} width={viewBoxWidth} height={bottomShadeHeight} />
            </g>
            <rect
                className={styles.canvasViewPort}
                x={canvasLeft}
                y={canvasTop}
                width={canvasWidth}
                height={canvasHeight}
            />
        </svg>
    );
});
